import { Checkbox, formatDate, Modal, SubmitButton, Table } from "@xola/ui-kit";
import { DateFormat, handleSuccessfulSubmission } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { LineItem, Purchase } from "types";
import { api } from "../../api/api";
import { useSeller } from "../../hooks/useAccount";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import { getPaymentMethodName } from "../../models/Payment";
import { getStoreCreditLineItemsForReverseFunding } from "../../models/Purchase";
import { getArrivalTime } from "../../models/PurchaseItem";
import { Currency } from "../Common/Currency";
import { SecondaryButton } from "../Common/SecondaryButton";
import { getPaymentMethod } from "../Event/CancelBookings/EventBookingsTable";

interface ReverseFundingModalProps {
    isOpen: boolean;
    onClose: () => void;
    storeCreditPurchase: Purchase;
}

export const ReverseFundingModal = ({ isOpen, onClose, storeCreditPurchase }: ReverseFundingModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <ModalContent purchase={storeCreditPurchase} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    onClose: () => void;
    purchase: Purchase;
}

interface DeletePaymentPayload {
    storeCreditId: ID;
    lineItemIds: ID[];
}

const ModalContent = ({ onClose, purchase }: ModalContentProps) => {
    const invalidate = useInvalidate();
    const [checkedIds, setCheckedIds] = useState<ID[]>([]);

    const {
        mutateAsync: deletePaymentLineItem,
        isLoading,
        isSuccess,
    } = useMutation(async (payload: DeletePaymentPayload) => {
        return Promise.all(
            payload.lineItemIds.map(async (id) => api.delete(`/purchases/${payload.storeCreditId}/lineItems/${id}`)),
        );
    });

    const handleSubmit = async () => {
        if (checkedIds.length > 0) {
            await deletePaymentLineItem({ storeCreditId: purchase.id, lineItemIds: checkedIds });
            handleSuccessfulSubmission(() => {
                invalidate("purchases", "collection");
                onClose();
            });
        }
    };

    const handleLineItemChecked = (lineItem: LineItem, checked: boolean) => {
        setCheckedIds(checked ? [...checkedIds, lineItem.id] : [...checkedIds.filter((id) => id !== lineItem.id)]);
    };

    const lineItems = getStoreCreditLineItemsForReverseFunding(purchase);
    const isSelectAllChecked = checkedIds.length === lineItems.length;

    const handleSelectAll = () => {
        if (isSelectAllChecked) {
            setCheckedIds([]);
        } else {
            setCheckedIds(lineItems.map((lineItem) => lineItem.id));
        }
    };

    return (
        <>
            <Modal.Header>Reverse Funding</Modal.Header>
            <Modal.Body>
                <Table className="border-none">
                    <Table.Head>
                        <Table.Row>
                            <Table.Header>Experience</Table.Header>
                            <Table.Header>Arrival</Table.Header>
                            <Table.Header>Arrival Time</Table.Header>
                            <Table.Header>Payment Method</Table.Header>
                            <Table.Header>Transfer Value</Table.Header>
                            <Table.Header>
                                <div className="flex flex-col items-center">
                                    <span className="whitespace-nowrap">Select All</span>
                                    {lineItems.length > 1 ? (
                                        <Checkbox checked={isSelectAllChecked} onChange={handleSelectAll} />
                                    ) : null}
                                </div>
                            </Table.Header>
                        </Table.Row>
                    </Table.Head>
                    <Table.Body>
                        {lineItems.map((lineItem) => {
                            return (
                                <LineItemTableRow
                                    key={lineItem.id}
                                    lineItem={lineItem}
                                    checked={checkedIds.includes(lineItem.id)}
                                    onChange={handleLineItemChecked}
                                />
                            );
                        })}
                    </Table.Body>
                </Table>
            </Modal.Body>
            <Modal.Footer className="flex justify-end space-x-4">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                <SubmitButton
                    isLoading={isLoading}
                    isSuccess={isSuccess}
                    disabled={checkedIds.length === 0}
                    onClick={handleSubmit}
                >
                    Confirm
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};

interface LineItemTableRowProps {
    lineItem: LineItem;
    checked: boolean;
    onChange: (lineItem: LineItem, checked: boolean) => void;
}

const LineItemTableRow = ({ lineItem, checked, onChange }: LineItemTableRowProps) => {
    const seller = useSeller();
    const { data: purchase } = useResource<Purchase>(["purchases", lineItem.fundingSource?.id]);

    if (!purchase) {
        return null;
    }

    const item = purchase.items[0];
    const handleChange = (event: any) => {
        onChange(lineItem, event.target.checked);
    };

    const arrivalDate = "arrivalDate" in item ? formatDate(item.arrivalDate, DateFormat.DATE_LONG) : "--";
    const arrivalTime = "arrivalDateTime" in item ? getArrivalTime(item) : "--";

    return (
        <Table.Row data-testid="reverse-refunding-modal-row">
            <Table.Cell data-testid="reverse-refunding-modal-experience-cell">
                <span className="break-words">
                    {purchase.items.length > 1 ? `Multiple Experiences (${item.name})` : item.name}
                </span>
            </Table.Cell>
            <Table.Cell data-testid="reverse-refunding-modal-arrival-date-cell">{arrivalDate}</Table.Cell>
            <Table.Cell data-testid="reverse-refunding-modal-arrival-time-cell">{arrivalTime}</Table.Cell>
            <Table.Cell data-testid="reverse-refunding-modal-payment-method-cell">
                {getPaymentMethodName(seller, getPaymentMethod(purchase.lineItems))}
            </Table.Cell>
            <Table.Cell data-testid="reverse-refunding-modal-amount-cell">
                <Currency>{Math.abs(lineItem.amount)}</Currency>
            </Table.Cell>
            <Table.Cell data-testid="reverse-refunding-modal-row-checkbox">
                <Checkbox className="justify-center" label="" checked={checked} onChange={handleChange} />
            </Table.Cell>
        </Table.Row>
    );
};
